import * as THREE from 'three';
import { SHIP_CLASS_KEY, type ShipClass } from '@viral-space/shared';
import { buildShipMesh, type ShipClassId, type ShipBuildOptions, type BuiltShip } from '../game/ship-builder';

/**
 * Identifier for a ship to render. Accepts either the canonical
 * lowercase slug (`'cruiser'`, `'fighter'`, …), or the numeric
 * `ShipClass` enum value (3, 1, …) which the rest of the app already
 * uses on the wire and in player-state.
 *
 * Accepting both forms is deliberate: callers like `char-select.ts`
 * naturally hold a `ShipClass` enum (it's what's saved on the account
 * record), while admin-style UIs prefer the slug. Forcing every caller
 * to remember `SHIP_CLASS_KEY[ShipClass.Cruiser]` is exactly the trap
 * that produced the "every saved pilot renders as a Fighter" bug —
 * `String(ShipClass.Cruiser)` silently returned `"3"`, which isn't
 * a key in `buildShipMesh`'s `BUILDERS` map, so the lookup fell
 * through to the Fighter default.
 */
export type ShipPreviewClass = string | ShipClass;

export interface ShipCardPreviewOptions extends ShipBuildOptions {
  /** Ship class to display. Falls back to "fighter" when unknown. */
  shipClass: ShipPreviewClass;
  /** Whether to rotate the ship at construction time. Default false. */
  rotating?: boolean;
}

export interface ShipCardPreviewHandle {
  /** Toggle the slow auto-rotation (used to highlight the selected card). */
  setRotating(on: boolean): void;
  /** Replace the displayed ship without recreating the renderer. */
  setShip(shipClass: ShipPreviewClass, opts?: ShipBuildOptions): void;
  /** Free the WebGL context, geometries, and observers. */
  dispose(): void;
}

/**
 * Normalise the dual-form `shipClass` input into a string slug that
 * `buildShipMesh`'s `BUILDERS` map understands. Mirrors
 * `entity-renderer.ts`'s `resolveShipClassSlug` (kept duplicated here
 * because the two modules don't share a util file and the conversion
 * is three lines).
 */
function resolveShipPreviewSlug(raw: ShipPreviewClass | undefined): string {
  if (typeof raw === 'string' && raw.length > 0) return raw;
  if (typeof raw === 'number') {
    const slug = (SHIP_CLASS_KEY as Record<number, string>)[raw];
    if (slug) return slug;
  }
  return 'fighter';
}

/**
 * Mount a small Three.js ship preview into the given canvas. Designed
 * for the pilot-select / character-create cards: every card gets its
 * own renderer, but only the *selected* card actually animates — unset
 * cards render exactly one frame and then idle, so the GPU stays cool
 * even with many cards on screen.
 *
 * Browsers cap concurrent WebGL contexts at ~16, which is well above
 * any character roster we ship today (max 5 saved pilots).
 */
export function mountShipCardPreview(
  canvas: HTMLCanvasElement,
  options: ShipCardPreviewOptions,
): ShipCardPreviewHandle {
  const renderer = new THREE.WebGLRenderer({
    canvas,
    antialias: true,
    alpha: true,
    powerPreference: 'low-power',
  });
  renderer.setClearColor(0x000000, 0);
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 1.75));

  const scene = new THREE.Scene();
  const camera = new THREE.PerspectiveCamera(38, 1, 0.1, 100);
  camera.position.set(2.4, 1.5, 5.6);
  camera.lookAt(0, 0, 0);

  scene.add(new THREE.AmbientLight(0x334466, 0.7));
  const key = new THREE.DirectionalLight(0x88ccff, 1.1);
  key.position.set(4, 6, 5);
  scene.add(key);
  const rim = new THREE.DirectionalLight(0x66ffcc, 0.65);
  rim.position.set(-6, -2, -3);
  scene.add(rim);
  const warm = new THREE.PointLight(0xff8855, 0.5, 14, 2);
  warm.position.set(-2, -1, 3);
  scene.add(warm);

  let current: BuiltShip | null = null;
  function applyShip(cls: ShipPreviewClass, opts?: ShipBuildOptions): void {
    if (current) {
      scene.remove(current.group);
      current.group.traverse((o) => {
        const mesh = o as THREE.Mesh;
        if (mesh.geometry) mesh.geometry.dispose();
        const m = mesh.material as THREE.Material | THREE.Material[] | undefined;
        if (Array.isArray(m)) m.forEach((mat) => mat.dispose());
        else if (m) m.dispose();
      });
    }
    current = buildShipMesh(resolveShipPreviewSlug(cls) as ShipClassId, opts);
    current.group.rotation.y = -0.5;
    current.group.rotation.x = 0.18;
    for (const mat of current.thrusterCores) mat.emissiveIntensity = 1.4;
    scene.add(current.group);
  }

  applyShip(options.shipClass, options);

  let rotating = !!options.rotating;
  let visible = true;
  let disposed = false;
  const clock = new THREE.Clock();

  function resize(): void {
    const rect = canvas.getBoundingClientRect();
    const w = Math.max(rect.width, 1);
    const h = Math.max(rect.height, 1);
    renderer.setSize(w, h, false);
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
  }
  resize();

  const ro = new ResizeObserver(() => {
    resize();
    drawOnce();
  });
  ro.observe(canvas);
  const io = new IntersectionObserver((entries) => {
    for (const e of entries) visible = e.isIntersecting;
    if (visible && !rotating) drawOnce();
  });
  io.observe(canvas);

  function drawOnce(): void {
    if (disposed || !current) return;
    renderer.render(scene, camera);
  }

  function loop(): void {
    if (disposed) return;
    if (!rotating) return;
    requestAnimationFrame(loop);
    if (!visible || !current) return;
    const t = clock.getElapsedTime();
    current.group.rotation.y += 0.01;
    current.group.rotation.x = 0.18 + Math.sin(t * 0.5) * 0.06;
    current.group.position.y = Math.sin(t * 0.7) * 0.07;
    const pulse = 1.4 + Math.sin(t * 4.5) * 0.6;
    for (const m of current.thrusterCores) m.emissiveIntensity = pulse;
    renderer.render(scene, camera);
  }

  drawOnce();
  if (rotating) loop();

  return {
    setRotating(on: boolean) {
      if (on === rotating) return;
      rotating = on;
      if (on) {
        clock.start();
        loop();
      } else {
        // Hold the ship in a clean static pose so it reads as inactive.
        if (current) {
          current.group.rotation.x = 0.18;
          current.group.position.y = 0;
          for (const m of current.thrusterCores) m.emissiveIntensity = 1.4;
        }
        drawOnce();
      }
    },
    setShip(cls: ShipPreviewClass, opts?: ShipBuildOptions) {
      applyShip(cls, opts);
      drawOnce();
    },
    dispose() {
      if (disposed) return;
      disposed = true;
      ro.disconnect();
      io.disconnect();
      renderer.dispose();
      if (current) {
        current.group.traverse((o) => {
          const mesh = o as THREE.Mesh;
          if (mesh.geometry) mesh.geometry.dispose();
          const m = mesh.material as THREE.Material | THREE.Material[] | undefined;
          if (Array.isArray(m)) m.forEach((mat) => mat.dispose());
          else if (m) m.dispose();
        });
      }
    },
  };
}
